// Pure helpers for showing AI-reviewed Apex answers in reports.
// Kept free of React so they can be unit-tested (see apexReport.test.js).

// A problem counts as "solved" at this overall score. Mirrors APEX_PASS_THRESHOLD in
// BackEnd/app/constants/apex.py.
export const APEX_PASS_THRESHOLD = 70;

export const isApexProblem = (problem) => problem?.track === "apex";

export const isApexSubmitted = (submission) =>
  Boolean(submission?.is_submitted ?? (submission?.language && submission?.code));

// A submitted answer with no status yet is treated as still being reviewed.
export const apexStatus = (submission) => {
  if (!isApexSubmitted(submission)) return "not_attempted";
  return submission.evaluation_status || "pending";
};

export const apexScore = (submission) =>
  apexStatus(submission) === "completed" && typeof submission.coding_score === "number"
    ? submission.coding_score
    : null;

/**
 * Overall picture for a set of Apex problems.
 *   avg: mean score over problems whose review is known (unattempted counts as 0);
 *        null while nothing has been reviewed yet.
 */
export function summarizeApex(problems, submissions = {}) {
  const summary = {
    total: problems.length,
    attempted: 0,
    reviewed: 0,
    pending: 0,
    failed: 0,
    avg: null,
    solved: [],
    needs: [],
  };
  let scoreSum = 0;
  let scored = 0;

  problems.forEach((problem) => {
    const submission = submissions[problem.id];
    const status = apexStatus(submission);

    if (status === "not_attempted") {
      scored += 1; // counts as 0
      summary.needs.push(problem.title);
      return;
    }
    summary.attempted += 1;

    if (status === "pending") { summary.pending += 1; return; }
    if (status === "failed") { summary.failed += 1; return; }

    const score = apexScore(submission) ?? 0;
    summary.reviewed += 1;
    scored += 1;
    scoreSum += score;
    (score >= APEX_PASS_THRESHOLD ? summary.solved : summary.needs).push(problem.title);
  });

  summary.avg = scored > 0 && (summary.reviewed > 0 || summary.attempted === 0) ? Math.round(scoreSum / scored) : null;
  return summary;
}

// An answer still "pending" long after submission means the background review was lost
// (e.g. a server restart), so the report offers/starts a retry.
export const STALE_PENDING_MS = 3 * 60 * 1000;

export function hasStalePending(problems, submissions = {}, now = Date.now()) {
  return problems.some((problem) => {
    const submission = submissions[problem.id];
    if (apexStatus(submission) !== "pending") return false;
    const submittedAt = submission.submitted_at ? new Date(submission.submitted_at).getTime() : NaN;
    return Number.isFinite(submittedAt) && now - submittedAt > STALE_PENDING_MS;
  });
}

export const hasAnyPending = (problems, submissions = {}) =>
  problems.some((problem) => apexStatus(submissions[problem.id]) === "pending");

export const hasAnyFailed = (problems, submissions = {}) =>
  problems.some((problem) => apexStatus(submissions[problem.id]) === "failed");
